// BUILD-046 increment 1a: how the Assigned by Me table is ORDERED - the comparators behind its Sort dropdown and its clickable column headings (Task, Assigned to, Status / progress,
// Priority, Due, Assigned / age). It orders REQUESTS (issuedRequestsModel's rows), so it is a page-owned module beside taskOrdering.ts and does not touch the legacy sort
// (sortModel.ts) or the task views' ordering.
//
// One state: the page's Sort choice (pageSortModel.ts) is the SAME state the dropdown and the headings read and write, exactly as in the other views. `orderIssuedRows` takes
// that choice; a choice Assigned by Me does not offer (a Team-only or task-view mode) is Recommended here, which leaves the list in the order it has always had (the server's,
// newest first, hints first): Recommended is NOT re-sorted.
//
//   Task            A-Z / Z-A on the stored title (case-insensitive, numbers in numeric order, accents ignored: the collation of taskOrdering.ts).
//   Assigned to     A-Z / Z-A on the FIRST name listed in the Assigned to cell: the assignee, the claimant, the first group of an Anyone request nobody has taken, the first
//                   recipient of an Everyone request (in the order the cell lists them). It is the first name only - a request that names several people is placed by the first.
//   Status / progress   ONE order, ascending: Unclaimed, Active, Waiting, Blocked, Completed, Cancelled. An Everyone request counts as Active until every recipient is done (then
//                   Completed) and as Cancelled when the request was cancelled; inside one band the request with the smaller SHARE DONE comes first (a Single or Anyone task is
//                   0, or 1 when completed). Descending is the exact reverse (Cancelled first ... Unclaimed last).
//   Priority        highest first (Urgent..Low) / lowest first. An Everyone request carries no priority in the request list, and a restricted task hides it: both are last.
//   Due             earliest first / latest first by calendar date. An Everyone request carries no due date in the request list, a restricted task hides it, and a task with none
//                   has none: all last.
//   Assigned / age  newest first / oldest first by the ORIGINAL assignment instant (the request's createdAt, never an update time). A request without a usable time is last.
//
// MISSING VALUES ARE LAST IN BOTH DIRECTIONS. TIES ARE DETERMINISTIC: rows equal on the key are put in request id order (ascending, in both directions); the arrival index only decides
// two rows with the same id, which the list never holds. Sorting covers the LOADED requests only (the list is read page by page).
//
// ORDERED RULES (BUILD-046 section D, personal Presets; Owner direction 2026-10-22). A preset may order Assigned by Me by up to three rules, for example Priority, then Task due date. `sortIssuedRowsByRules` compares
// two requests rule by rule: the first rule whose values differ decides; a request with no value for a rule is last under THAT rule, in both directions, and the next rule then orders requests that are
// equal or both missing on it; the last tie is the request id. It reads the same request-level values the single orders read (`issuedSortKeys`) and invents none: an Everyone request has no priority or Task due
// date in the request list and a restricted task hides them, so under a Priority or Due rule those requests are last, never placed by a guess. A single rule gives exactly the single order above.
//
// Pure module. Runtime imports: the sort keys of issuedRowModel.ts and the mode logic of pageSortModel.ts. Type-only imports otherwise. Erasable TypeScript only.

import type { IssuedRow } from './issuedRequestsModel';
import { issuedSortKeys } from './issuedRowModel';
import type { IssuedSortKeys } from './issuedRowModel';
import { effectivePageSortMode } from './pageSortModel';
import type { PageSortMode } from './pageSortModel';
import type { OrderDirection } from './taskOrdering';

export type IssuedOrderKey = 'title' | 'responsible' | 'progress' | 'priority' | 'due' | 'assigned';

/** What each Sort mode of Assigned by Me orders by (Recommended is absent: it is the list's own order). 'asc' / 'desc' are the DATA directions, as in taskOrdering.ts. */
export const ISSUED_MODE_ORDER: Readonly<Partial<Record<PageSortMode, { key: IssuedOrderKey; direction: OrderDirection }>>> = Object.freeze({
  'title-asc': { key: 'title', direction: 'asc' },
  'title-desc': { key: 'title', direction: 'desc' },
  'responsible-asc': { key: 'responsible', direction: 'asc' },
  'responsible-desc': { key: 'responsible', direction: 'desc' },
  'progress-asc': { key: 'progress', direction: 'asc' },
  'progress-desc': { key: 'progress', direction: 'desc' },
  priority: { key: 'priority', direction: 'desc' },
  'priority-low': { key: 'priority', direction: 'asc' },
  'due-soonest': { key: 'due', direction: 'asc' },
  'due-latest': { key: 'due', direction: 'desc' },
  'assigned-newest': { key: 'assigned', direction: 'desc' },
  'assigned-oldest': { key: 'assigned', direction: 'asc' },
});

const COLLATOR = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

const byNumber = (a: number, b: number): number => a - b;
const byDate = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);
const byText = (a: string, b: string): number => COLLATOR.compare(a, b);

interface KeySpec {
  /** True when the request has a value for the key (a request without one is last, whichever way the order runs). */
  present: (keys: IssuedSortKeys) => boolean;
  /** The ascending order of two requests that both have a value. */
  compare: (a: IssuedSortKeys, b: IssuedSortKeys) => number;
}

function keySpec<V>(valueOf: (keys: IssuedSortKeys) => V | null, compare: (a: V, b: V) => number): KeySpec {
  return { present: (keys) => valueOf(keys) !== null, compare: (a, b) => compare(valueOf(a) as V, valueOf(b) as V) };
}

/** What each key orders by: the ONE definition behind a single column order and behind an ordered list of rules. */
const KEY_SPECS: Readonly<Record<IssuedOrderKey, KeySpec>> = Object.freeze({
  title: keySpec((keys) => keys.title, byText),
  responsible: keySpec((keys) => keys.responsible, byText),
  progress: keySpec((keys) => keys.progress, (a, b) => a.band - b.band || a.share - b.share),
  priority: keySpec((keys) => keys.priority, byNumber),
  due: keySpec((keys) => keys.due, byDate),
  assigned: keySpec((keys) => keys.assigned, byNumber),
});

/** The keys a rule may name on Assigned by Me, in the order the Presets panel lists them. */
export const ISSUED_ORDER_KEYS: readonly IssuedOrderKey[] = Object.freeze(['title', 'responsible', 'progress', 'priority', 'due', 'assigned'] as IssuedOrderKey[]);

export interface IssuedRule {
  key: IssuedOrderKey;
  direction: OrderDirection;
}

/** The rules among `raw` that Assigned by Me can use, in order; a key it does not have (a task-table key) is dropped. */
export function issuedRulesOf(raw: readonly { key: string; direction: OrderDirection }[]): IssuedRule[] {
  return raw.filter((rule) => (ISSUED_ORDER_KEYS as readonly string[]).includes(rule.key)).map((rule) => ({ key: rule.key as IssuedOrderKey, direction: rule.direction }));
}

/** A new array: `rows` ordered by the rules (first rule first), a missing value last under its rule, the last tie the request id. The input array is not changed. */
export function sortIssuedRowsByRules(rows: readonly IssuedRow[], rules: readonly IssuedRule[]): IssuedRow[] {
  const decorated = rows.map((row, index) => ({ row, index, keys: issuedSortKeys(row) }));
  decorated.sort((x, y) => {
    for (const rule of rules) {
      const spec = KEY_SPECS[rule.key];
      if (spec === undefined) continue;
      const hasX = spec.present(x.keys);
      const hasY = spec.present(y.keys);
      if (!hasX && !hasY) continue;
      if (!hasX) return 1;
      if (!hasY) return -1;
      const raw = spec.compare(x.keys, y.keys);
      const signed = rule.direction === 'asc' ? raw : -raw;
      if (signed !== 0) return signed;
    }
    return x.row.requestId < y.row.requestId ? -1 : x.row.requestId > y.row.requestId ? 1 : x.index - y.index;
  });
  return decorated.map((entry) => entry.row);
}

/** A new array: `rows` ordered by `key` in `direction`, missing values last, ties by request id. The input array is not changed. */
export function sortIssuedRows(rows: readonly IssuedRow[], key: IssuedOrderKey, direction: OrderDirection): IssuedRow[] {
  return KEY_SPECS[key] === undefined ? [...rows] : sortIssuedRowsByRules(rows, [{ key, direction }]);
}

/**
 * The rows in the order the page's Sort choice asks for. `chosen` is the page's saved choice (any value): a mode Assigned by Me does not offer is Recommended, and Recommended is the
 * rows as they came (the list's own order, unchanged). A new array; the input is not changed.
 */
export function orderIssuedRows(rows: readonly IssuedRow[], chosen: unknown, rules: readonly IssuedRule[] = []): IssuedRow[] {
  if (rules.length > 0) return sortIssuedRowsByRules(rows, rules);
  const mode = effectivePageSortMode(chosen, 'issued');
  const order = ISSUED_MODE_ORDER[mode];
  return order === undefined ? [...rows] : sortIssuedRows(rows, order.key, order.direction);
}
